import type {CSSProperties} from 'react';
import {font, radius, space, weight} from '../scale.js';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeMeetingFooterLook = 'invite-reconnect' | 'invite-skip' | 'invite-connect';

const FOOTER_LOOKS = new Set<string>(['invite-reconnect', 'invite-skip', 'invite-connect']);
export const isWedgeMeetingFooterLook = (look: string): look is WedgeMeetingFooterLook => FOOTER_LOOKS.has(look);

/* Released Schedule meeting sheet, error banner and footer: three native buttons. The banner shows when Google refuses the invite for a
   missing write scope. Reconnect Google Calendar is an 8px radius block with 8px by 12px padding and 12.5px bold type, on the amber fill
   with the on-accent ink and no border. Save without sending is the same box with no fill, the secondary ink, a 1px separator border and
   semibold type. Connect Google Calendar to send the invite sits in the footer, 8px under the invite switch: a 10px radius block with
   10px by 14px padding and 13px bold type, no fill, and the accent ink on a 1px accent border. The names start with `invite-`, and not
   with `meeting-`, so the shipped rule that drops the target of the body looks (`meeting-` except Add) does not reach them.
   Each reads the exact legacy roles and keeps the declaration order of the source, so the shipped ink rules keep matching the
   serialized text (the light accent ink rule matches `color: var(--accent);`). None sets a font family, as the source leaves it out;
   the native button face stays. The caller owns the labels and the handlers. All three keep the transparent 44px target and its anchor:
   see the figures in the look test. */
const NOTICE_FONT = 12.5, NOTICE_PADDING = '8px 12px', CONNECT_PADDING = '10px 14px';

export function wedgeMeetingFooterStyle(look: WedgeMeetingFooterLook): CSSProperties {
  const r = wedgeExactRole;
  switch (look) {
    case 'invite-reconnect': return {
      padding: NOTICE_PADDING, borderRadius: radius.sm, background: r('amber'), color: r('onAccent'), border: 'none', fontWeight: weight.bold,
      fontSize: NOTICE_FONT, cursor: 'pointer',
    };
    case 'invite-skip': return {
      padding: NOTICE_PADDING, borderRadius: radius.sm, background: 'transparent', color: r('labelSec'), border: `1px solid ${r('separator')}`,
      fontWeight: weight.semibold, fontSize: NOTICE_FONT, cursor: 'pointer',
    };
    case 'invite-connect': return {
      marginTop: space[2], padding: CONNECT_PADDING, borderRadius: radius.md, background: 'transparent', border: `1px solid ${r('accent')}`,
      color: r('accent'), fontWeight: weight.bold, fontSize: font.footnote, cursor: 'pointer',
    };
  }
}
